import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useWizardDraftStore } from '@/store/wizardDraftStore';

/**
 * Wizard-modus per wizard.
 *   `classic` = stand-alone per-view wizard (separate views per steg).
 *   `shell`   = wizard som bruker felles WizardShell + draft-store.
 *
 * Termene 'legacy' og 'next' brukes ikke i FE-kode — de er flyktige versjons-
 * merkelapper. BE-kontrakten (FeatureFlagsJson) bruker fortsatt strengen
 * 'legacy'/'next' av historiske grunner; hydreringen + persisteringen mapper
 * mellom representasjonene ved kontrakt-grensen (se useUserPreferencesHydration).
 */
export type WizardMode = 'classic' | 'shell';

export type WizardKind =
  | 'createFromFile'
  | 'importToExisting'
  | 'createImportFromFile'
  | 'createFromSetupFile';

const CREATE_FROM_FILE_STORAGE_KEY = 'createFromFileWizardMode';
const IMPORT_TO_EXISTING_STORAGE_KEY = 'importToExistingWizardMode';
const CREATE_IMPORT_FROM_FILE_STORAGE_KEY = 'createImportFromFileWizardMode';
const CREATE_FROM_SETUP_FILE_STORAGE_KEY = 'createFromSetupFileWizardMode';

/**
 * Subset av UserPreferences-featureFlags som hydrateFromBackend leser.
 * Feltene er strenger fra BE (verdier 'legacy' eller 'next' i nåværende
 * kontrakt) — coerceMode mapper til FE-representasjonen.
 */
export interface WizardModePreferenceFragment {
  featureFlags?: {
    createFromFileWizardMode?: string | null;
    importToExistingWizardMode?: string | null;
    createImportFromFileWizardMode?: string | null;
    createFromSetupFileWizardMode?: string | null;
  } | null;
}

export type WizardModeSaveCallback = (payload: {
  createFromFileMode: WizardMode;
  importToExistingMode: WizardMode;
  createImportFromFileMode: WizardMode;
  createFromSetupFileMode: WizardMode;
}) => void | Promise<void>;

/**
 * Aksepterer både ny ('classic'/'shell') og gammel ('legacy'/'next') verdi —
 * 'next'/'shell' → 'shell', alt annet → 'classic'. Brukes ved hydrering fra
 * BE og ved warm-restore fra localStorage.
 */
function coerceMode (value: unknown): WizardMode {
  if (value === 'shell' || value === 'next') return 'shell';
  return 'classic';
}

function loadStoredMode (key: string): WizardMode {
  try {
    const stored = window.localStorage.getItem(key);
    // Opt-out (WI 2258): ny opplevelse er standard når brukeren ikke har valgt noe.
    // En faktisk lagret verdi (også 'legacy') coerce-es som før.
    if (stored === null) return 'shell';
    return coerceMode(stored);
  } catch {
    return 'shell';
  }
}

function persistLocal (key: string, next: WizardMode): void {
  try {
    window.localStorage.setItem(key, next);
  } catch {
    // localStorage may be disabled
  }
}

export const useWizardModeToggleStore = defineStore('wizardModeToggle', () => {
  const createFromFileMode = ref<WizardMode>(loadStoredMode(CREATE_FROM_FILE_STORAGE_KEY));
  const importToExistingMode = ref<WizardMode>(loadStoredMode(IMPORT_TO_EXISTING_STORAGE_KEY));
  const createImportFromFileMode = ref<WizardMode>(loadStoredMode(CREATE_IMPORT_FROM_FILE_STORAGE_KEY));
  const createFromSetupFileMode = ref<WizardMode>(loadStoredMode(CREATE_FROM_SETUP_FILE_STORAGE_KEY));
  const isHydrating = ref<boolean>(false);

  let saveCallback: WizardModeSaveCallback | null = null;

  function setSaveCallback (callback: WizardModeSaveCallback | null): void {
    saveCallback = callback;
  }

  function emitSave (): void {
    if (isHydrating.value || !saveCallback) return;
    void saveCallback({
      createFromFileMode: createFromFileMode.value,
      importToExistingMode: importToExistingMode.value,
      createImportFromFileMode: createImportFromFileMode.value,
      createFromSetupFileMode: createFromSetupFileMode.value,
    });
  }

  /**
   * Toggle classic↔shell mellom mid-draft må slette den aktive draften slik
   * at bruker ikke ender opp med en zombie-state i den andre modusen
   * (smoketest 2026-05-20). Hopper over under hydrering for å ikke trigge
   * sletting når warm-cache leses inn på app-start.
   */
  function cancelActiveDraftIfAny (): void {
    if (isHydrating.value) return;
    try {
      const draftStore = useWizardDraftStore();
      if (draftStore.publicId !== null || draftStore.draftId !== null) {
        void draftStore.cancel().catch(() => undefined);
      }
    } catch {
      // useWizardDraftStore krever aktiv pinia; ved sjeldne race-tilfeller (eks.
      // teardown) er det ikke kritisk å rydde — TTL rydder uansett.
    }
  }

  function setCreateFromFileMode (next: WizardMode): void {
    cancelActiveDraftIfAny();
    createFromFileMode.value = next;
    persistLocal(CREATE_FROM_FILE_STORAGE_KEY, next);
    emitSave();
  }

  function setImportToExistingMode (next: WizardMode): void {
    cancelActiveDraftIfAny();
    importToExistingMode.value = next;
    persistLocal(IMPORT_TO_EXISTING_STORAGE_KEY, next);
    emitSave();
  }

  function setCreateImportFromFileMode (next: WizardMode): void {
    cancelActiveDraftIfAny();
    createImportFromFileMode.value = next;
    persistLocal(CREATE_IMPORT_FROM_FILE_STORAGE_KEY, next);
    emitSave();
  }

  function setCreateFromSetupFileMode (next: WizardMode): void {
    cancelActiveDraftIfAny();
    createFromSetupFileMode.value = next;
    persistLocal(CREATE_FROM_SETUP_FILE_STORAGE_KEY, next);
    emitSave();
  }

  function resetToDefaults (): void {
    // Opt-out (WI 2258): standarden er 'shell'.
    setCreateFromFileMode('shell');
    setImportToExistingMode('shell');
    setCreateImportFromFileMode('shell');
    setCreateFromSetupFileMode('shell');
  }

  /**
   * Hydrer alle wizard-modi fra backend-preferences. BE wins per felt hvis
   * verdien er gyldig (etter coerce); fallback til eksisterende state ellers.
   */
  function hydrateFromBackend (prefs: WizardModePreferenceFragment | null | undefined): void {
    if (!prefs) return;

    isHydrating.value = true;
    try {
      const createFromFile = prefs.featureFlags?.createFromFileWizardMode;
      if (createFromFile != null) {
        setCreateFromFileMode(coerceMode(createFromFile));
      }

      const importToExisting = prefs.featureFlags?.importToExistingWizardMode;
      if (importToExisting != null) {
        setImportToExistingMode(coerceMode(importToExisting));
      }

      const createImportFromFile = prefs.featureFlags?.createImportFromFileWizardMode;
      if (createImportFromFile != null) {
        setCreateImportFromFileMode(coerceMode(createImportFromFile));
      }

      const createFromSetupFile = prefs.featureFlags?.createFromSetupFileWizardMode;
      if (createFromSetupFile != null) {
        setCreateFromSetupFileMode(coerceMode(createFromSetupFile));
      }
    } finally {
      isHydrating.value = false;
    }
  }

  return {
    createFromFileMode,
    importToExistingMode,
    createImportFromFileMode,
    createFromSetupFileMode,
    isHydrating,
    setCreateFromFileMode,
    setImportToExistingMode,
    setCreateImportFromFileMode,
    setCreateFromSetupFileMode,
    resetToDefaults,
    hydrateFromBackend,
    setSaveCallback,
  };
});
